import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { http, formatErr } from "@/lib/api";
import { useAuth, can, ROLE_LABEL } from "@/lib/auth";
import { StatusBadge } from "@/components/StatusBadge";
import { CaretLeft, CaretRight, Calendar as CalendarIcon, FunnelSimple, ArrowsOutCardinal, ListChecks, NotePencil } from "@phosphor-icons/react";

const STATUS_BG = {
  submitted: "#3B82F6",
  pending_approval: "#F59E0B",
  approved: "#10B981",
  assigned: "#6366F1",
  in_progress: "#0EA5E9",
  completed: "#22C55E",
  closed: "#94A3B8",
  rejected: "#E11D48",
  draft: "#CBD5E1",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function ukDow(d) { return (d.getDay() + 6) % 7; } // Monday = 0
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

export default function Scheduled() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = can(user, "jobs", "edit");
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [dragId, setDragId] = useState(null);
  const [rescheduleErr, setRescheduleErr] = useState("");
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: () => http.get("/jobs").then(r => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data).catch(() => []) });
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: () => http.get("/tasks").then(r => r.data).catch(() => []) });

  const techUsers = users.filter(u => ["maintenance_staff", "contractor"].includes(u.role));

  const matchesAssignee = (j) => {
    if (!assigneeFilter) return true;
    if (j.assigned_to_id === assigneeFilter) return true;
    return (j.assignee_pool || []).some(p => p.user_id === assigneeFilter);
  };

  const visibleJobs = useMemo(() => jobs.filter(matchesAssignee), [jobs, assigneeFilter]);

  // dated tasks/reminders (visibility already enforced by the API), honouring the assignee filter
  const visibleTasks = useMemo(
    () => tasks.filter(t => t.date && (!assigneeFilter || t.assignee_id === assigneeFilter)),
    [tasks, assigneeFilter]
  );

  const reschedule = async (jobId, dateCell) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    setRescheduleErr("");
    // preserve original time-of-day, just move the date
    const orig = job.due_date ? new Date(job.due_date) : new Date();
    const next = new Date(dateCell.getFullYear(), dateCell.getMonth(), dateCell.getDate(), orig.getHours(), orig.getMinutes());
    try {
      await http.post(`/jobs/${jobId}/reschedule`, { due_date: next.toISOString(), expected_updated_at: job.updated_at });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    } catch (e) { setRescheduleErr(formatErr(e?.response?.data?.detail)); }
  };

  const grid = useMemo(() => {
    const days = daysInMonth(cursor);
    const leadingBlanks = ukDow(cursor);
    const cells = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const jobsByDay = useMemo(() => {
    const m = new Map();
    for (const j of visibleJobs) {
      if (!j.due_date || j.status === "closed" || j.status === "completed") continue;
      const dt = new Date(j.due_date);
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(j);
    }
    return m;
  }, [visibleJobs]);

  const tasksByDay = useMemo(() => {
    const m = new Map();
    for (const t of visibleTasks) {
      const dt = new Date(t.date);
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(t);
    }
    return m;
  }, [visibleTasks]);

  const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const today = new Date();

  const upcoming = visibleJobs.filter(j => ["approved", "assigned", "in_progress"].includes(j.status))
                       .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Pipeline</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1 flex items-center gap-3"><CalendarIcon size={24} /> Calendar</h1>
          {canEdit && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><ArrowsOutCardinal size={12} /> Drag a job to another day to reschedule it.</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <FunnelSimple size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} data-testid="cal-assignee-filter"
              className="h-9 pl-8 pr-3 text-sm border border-slate-200 rounded-md bg-white min-w-[180px]">
              <option value="">All technicians</option>
              {techUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({ROLE_LABEL[u.role]})</option>)}
            </select>
          </div>
          <button data-testid="cal-prev" aria-label="Previous month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="h-9 w-9 inline-flex items-center justify-center border border-slate-200 rounded-md hover:bg-slate-50"><CaretLeft size={14} /></button>
          <div className="h-9 px-4 inline-flex items-center text-sm font-medium border border-slate-200 rounded-md font-heading" data-testid="cal-month-label">{monthLabel}</div>
          <button data-testid="cal-next" aria-label="Next month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="h-9 w-9 inline-flex items-center justify-center border border-slate-200 rounded-md hover:bg-slate-50"><CaretRight size={14} /></button>
          <button data-testid="cal-today" onClick={() => setCursor(startOfMonth(new Date()))} className="h-9 px-3 text-xs font-heading uppercase tracking-wider border border-slate-200 rounded-md hover:bg-slate-50">Today</button>
        </div>
      </div>

      {rescheduleErr && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-2 mb-4" data-testid="cal-reschedule-error">{rescheduleErr}</div>}

      <div className="rmms-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
            <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-heading font-semibold text-slate-500 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((cell, i) => {
            if (!cell) return <div key={`blank-${cursor.getFullYear()}-${cursor.getMonth()}-${i}`} className="h-28 sm:h-32 bg-slate-50/40 border-t border-l border-slate-200" />;
            const key = `${cell.getFullYear()}-${cell.getMonth()}-${cell.getDate()}`;
            const dayJobs = jobsByDay.get(key) || [];
            const dayTasks = tasksByDay.get(key) || [];
            const isToday = sameDay(cell, today);
            const overflow = Math.max(dayJobs.length - 3, 0) + Math.max(dayTasks.length - 2, 0);
            return (
              <div key={key} data-testid={`cal-day-${cell.getDate()}`}
                   onDragOver={canEdit ? (e) => e.preventDefault() : undefined}
                   onDrop={canEdit ? () => { if (dragId) { reschedule(dragId, cell); setDragId(null); } } : undefined}
                   className={`h-28 sm:h-32 border-t border-l border-slate-200 p-1.5 overflow-hidden ${isToday ? "bg-blue-50/40" : ""}`}>
                <div className={`text-xs font-mono-data tabular-nums mb-1 ${isToday ? "font-bold text-[#002FA7]" : "text-slate-500"}`}>{cell.getDate()}</div>
                <div className="space-y-0.5">
                  {dayJobs.slice(0, 3).map(j => (
                    <Link to={`/jobs/${j.id}`} key={j.id} data-testid={`cal-event-${j.job_number}`}
                      draggable={canEdit}
                      onDragStart={canEdit ? (e) => { setDragId(j.id); e.dataTransfer.effectAllowed = "move"; } : undefined}
                      className={`block text-[10.5px] truncate px-1.5 py-0.5 rounded-sm hover:opacity-80 transition-opacity text-white font-medium leading-tight ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
                      style={{ background: STATUS_BG[j.status] || "#64748B" }}>
                      {j.title}
                    </Link>
                  ))}
                  {dayTasks.slice(0, 2).map(t => (
                    <Link to="/tasks" key={`task-${t.id}`} data-testid={`cal-task-${t.id}`} title={`${t.kind === "note" ? "Note" : "Task"}: ${t.title}`}
                      className={`flex items-center gap-1 text-[10.5px] truncate px-1.5 py-0.5 rounded-sm border leading-tight hover:opacity-80 transition-opacity ${
                        t.kind === "note" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-white border-slate-300 text-slate-700"
                      } ${t.done ? "line-through text-slate-400" : ""}`}>
                      {t.kind === "note" ? <NotePencil size={9} weight="fill" className="shrink-0" /> : <ListChecks size={9} weight="bold" className="shrink-0" />}
                      <span className="truncate">{t.title}</span>
                    </Link>
                  ))}
                  {overflow > 0 && <div className="text-[10px] text-slate-500 pl-1">+{overflow} more</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-slate-200 px-4 py-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
          {["pending_approval", "assigned", "in_progress", "submitted"].map(s => (
            <span key={s} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: STATUS_BG[s] }} />
              <span className="capitalize">{s.replace("_", " ")}</span>
            </span>
          ))}
          <span className="h-3 w-px bg-slate-200 mx-1" />
          <span className="flex items-center gap-1.5"><ListChecks size={12} weight="bold" className="text-slate-500" /> Task</span>
          <span className="flex items-center gap-1.5"><NotePencil size={12} weight="fill" className="text-amber-500" /> Note</span>
        </div>
      </div>

      <div className="mt-6">
        <div className="overline mb-2">Upcoming list</div>
        <div className="rmms-card divide-y divide-slate-100">
          {upcoming.slice(0, 8).map(j => (
            <Link to={`/jobs/${j.id}`} key={j.id} data-testid={`scheduled-row-${j.job_number}`}
              className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-3 hover:bg-slate-50 items-center transition-colors">
              <div>
                <div className="text-sm font-medium">{j.title}</div>
                <div className="text-xs text-slate-500 font-mono-data">{j.job_number}</div>
              </div>
              <div className="text-xs font-mono-data text-slate-600">Due {new Date(j.due_date).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</div>
              <StatusBadge status={j.status} />
            </Link>
          ))}
          {upcoming.length === 0 && <div className="px-5 py-12 text-center text-sm text-slate-500">Nothing scheduled.</div>}
        </div>
      </div>
    </div>
  );
}
